import React from 'react';
import { trackWhopBannerClick } from '../services/analyticsService';
import { getRefCode } from '../utils/localStorage';
// @ts-ignore
import hotPotatoIcon from '../image/hotPotato.png';

const WHOP_URL = 'https://whop.com/onetapsell/dump-alert-re-entry-service/?utm_source=X&utm_medium=landing&utm_campaign=hotpotato_alerts_launch&utm_content=cta_header&utm_term=hotpotato_alerts';

function openCenteredPopup(url: string) {
  const width = 520;
  const height = 720;

  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || window.screen.height;

  const left = Math.max(0, Math.floor(viewportWidth / 2 - width / 2 + dualScreenLeft));
  const top = Math.max(0, Math.floor(viewportHeight / 2 - height / 2 + dualScreenTop));

  const features = [
    'popup=yes',
    'toolbar=no',
    'location=no',
    'status=no',
    'menubar=no',
    'scrollbars=yes',
    'resizable=yes',
    `width=${width}`,
    `height=${height}`,
    `top=${top}`,
    `left=${left}`,
  ].join(',');

  const popup = window.open(url, 'onetapsell_whop_checkout', features);

  if (popup) {
    try {
      popup.opener = null;
      popup.focus();
    } catch (_) {}
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

export function WhopPromoBanner() {
  return (
    <a
      href={WHOP_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        trackWhopBannerClick(getRefCode());
        openCenteredPopup(WHOP_URL);
      }}
      className="group relative block overflow-visible rounded-2xl bg-gradient-to-r from-purple-900/90 via-indigo-900/90 to-black/90 p-[2px] shadow-[0_16px_60px_-18px_rgba(139,92,246,0.55)] transition-transform hover:-translate-y-0.5 hover:shadow-[0_22px_80px_-22px_rgba(139,92,246,0.75)] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/80"
      aria-label="Open Dump Alerts + Re-entry Service checkout"
    >
      {/* Icon can overflow outside */}
      <img
        src={hotPotatoIcon}
        alt="Hot Potato Alerts"
        className="pointer-events-none absolute -top-6 right-0 z-20 h-32 w-auto object-contain sm:-top-9 sm:h-40"
      />

      {/* Keep background/glow clipped to rounded card */}
      <div className="relative overflow-hidden rounded-2xl">
        {/* Colored line on top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 opacity-80 blur-sm" />

        {/* Glowing border effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 opacity-50 blur-md" />

        <div className="relative rounded-2xl bg-gradient-to-r from-purple-900/95 via-indigo-900/95 to-black/95 px-4 py-4 sm:px-5 sm:py-5">
          {/* Blur circles moved down */}
          <div className="pointer-events-none absolute top-12 right-0 h-48 w-48 rounded-full bg-white/10 blur-3xl transition-opacity group-hover:opacity-100" />
          <div className="pointer-events-none absolute bottom-12 left-0 h-48 w-48 rounded-full bg-white/10 blur-3xl opacity-70 transition-opacity group-hover:opacity-100" />

          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3 sm:items-center">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white"
                >
                  <path
                    d="M12 22s8-4 8-10V7l-8-3-8 3v5c0 6 8 10 8 10Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 12l2.5 2.5L16 9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className="min-w-0 pr-20 sm:pr-32">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-gray-800/80 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-gray-300 ring-1 ring-white/10">
                    New
                  </span>
                  <h3 className="truncate text-base font-semibold text-white sm:text-lg">
                    Hot potato alerts help you to make money every time the market crash
                  </h3>
                </div>
                <p className="mt-1 text-sm text-white/80">
                  Get real-time dump alerts and re-entry signals. Open a secure checkout popup to subscribe.
                </p>
              </div>
            </div>

            <div className="mt-1 flex items-center justify-between gap-3 sm:mt-3 sm:justify-end">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div
                  className="h-px flex-1 bg-gradient-to-r from-purple-500/70 via-fuchsia-500/60 to-transparent opacity-80"
                  aria-hidden="true"
                />
                <div className="flex items-center gap-2 text-xs text-white/70">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.15)]" />
                  <span>Instant access</span>
                </div>
              </div>

              <span className="inline-flex w-full items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm transition-all group-hover:bg-white/95 sm:w-auto">
                Unlock Access
                <svg
                  className="ml-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 17L17 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M9 7H17V15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}
